class BuyOnline {
  constructor(root) {
    this.root = root;
    this.productId = root.dataset.productId || root.dataset.variantId || '';
    this.productHandle = root.dataset.productHandle || '';
    this.endpoint = this.buildEndpoint(root.dataset.endpoint || '');
    this.openBtn = root.querySelector('[data-bo-open-drawer]');
    this.drawer = root.querySelector('[data-bo-drawer]');
    this.overlay = root.querySelector('[data-bo-overlay]');
    this.closeBtn = root.querySelector('[data-bo-close]');
    this.listEl = root.querySelector('[data-bo-list]');
    this.searchInput = root.querySelector('[data-bo-search]');
    this.nearbyBtn = root.querySelector('[data-bo-nearby]');
    this.selectedStoreEl = root.querySelector('[data-bo-selected-store]');
    this.selectedAddressEl = root.querySelector('[data-bo-selected-address]');
    this.selectedPhoneEl = root.querySelector('[data-bo-selected-phone]');
    this.stockWrapper = root.querySelector('.buy-online__stock');
    this.stockEl = root.querySelector('[data-bo-stock]');
    this.pickupStatusEl = root.querySelector('[data-bo-pickup-status]');
    this.shipStatusEl = root.querySelector('[data-bo-ship-status]');
    this.pickupTab = root.querySelector('[data-bo-mode="pickup"]');
    this.shipTab = root.querySelector('[data-bo-mode="ship"]');
    this.bodyEl = root.querySelector('.buy-online__body');
    this.pickupRows = root.querySelectorAll('[data-bo-selected-row]');
    this.selectedMode = 'pickup';
    this.locations = [];
    this.filtered = [];
    this.currentSelectionId = null;
    this.userCoordinates = null;
    this.hiddenLocationMap = this.getHiddenLocations();
    this.locationsLoaded = false;
    this.rawLocationData = null; // Store raw API response for online store detection
    this.onlineStoreCodes = this.getOnlineStoreCodes();
    this.updateCartAttributes = window.updatePickupCartAttributes;
    this.userSelectedShip = false; // Track if user explicitly selected ship mode
    this.userSelectedPickup = false; // Track if user explicitly selected pickup mode
    this.detachDrawerToBody();
    // Re-query overlay from drawer after it's moved to body
    if (this.drawer) {
      this.overlay = this.drawer.querySelector('[data-bo-overlay]');
    }
    this.attachEvents();
    this.syncSelectedStore();
    this.loadLocations();
  }

  buildEndpoint(raw) {
    if (!raw) return '';
    let urlString = raw;

    // Replace placeholders
    if (urlString.includes('__HANDLE__')) {
      urlString = urlString.replace(/__HANDLE__/g, this.productHandle || '');
    }
    if (urlString.includes('__PRODUCT_ID__')) {
      urlString = urlString.replace(/__PRODUCT_ID__/g, this.productId || '');
    }

    // Ensure handle/id are present even if placeholders weren't in the setting
    try {
      const url = new URL(urlString);
      if (!url.searchParams.get('handle') && this.productHandle) {
        url.searchParams.set('handle', this.productHandle);
      }
      if (!url.searchParams.get('id') && this.productId) {
        url.searchParams.set('id', this.productId);
      }
      return url.toString();
    } catch (_err) {
      // Fallback for invalid URL strings; append query manually
      const joiner = urlString.includes('?') ? '&' : '?';
      const extra = [];
      if (!urlString.includes('handle=') && this.productHandle) {
        extra.push(`handle=${encodeURIComponent(this.productHandle)}`);
      }
      if (!urlString.includes('id=') && this.productId) {
        extra.push(`id=${encodeURIComponent(this.productId)}`);
      }
      return extra.length ? `${urlString}${joiner}${extra.join('&')}` : urlString;
    }
  }

  detachDrawerToBody() {
    if (!this.drawer || this.drawer.dataset.detachedFromHost) return;
    this.drawer.dataset.detachedFromHost = 'true';
    document.body.appendChild(this.drawer);
    // Re-query overlay from drawer after it's moved to body
    this.overlay = this.drawer.querySelector('[data-bo-overlay]');
  }

  attachEvents() {
    this.openBtn?.addEventListener('click', () => this.openDrawer());
    this.overlay?.addEventListener('click', () => this.closeDrawer());
    this.closeBtn?.addEventListener('click', () => this.closeDrawer());
    this.nearbyBtn?.addEventListener('click', () => this.handleNearby());
    this.pickupTab?.addEventListener('click', () => this.selectMode('pickup'));
    this.shipTab?.addEventListener('click', () => this.selectMode('ship'));
    document.addEventListener('store-selector:change', (event) => {
      if (event.detail?.label) {
        this.setSelectedStore(event.detail.label);
      }
      if (event.detail?.id) {
        this.currentSelectionId = String(event.detail.id);
        this.setSelectedDetailsFromList(this.currentSelectionId);
        this.updateSelectionAvailability();
      }
    });
    
    // Initialize Google Maps autocomplete if API key is available, otherwise use fallback
    this.initSearchInput();
  }

  initSearchInput() {
    if (!this.searchInput) return;
    
    // Check if Google Maps API key is available
    const apiKey = window.inventoryInfo?.settings?.geo?.apiKey;
    
    if (apiKey) {
      // Load Google Maps Places API and initialize autocomplete
      this.initGoogleMapsAutocomplete(apiKey);
    } else {
      // Fall back to existing search functionality
      this.searchInput.addEventListener('input', (e) => this.onSearch(e));
    }
  }

  initGoogleMapsAutocomplete(apiKey) {
    // Check if Google Maps script is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      this.setupAutocomplete();
      return;
    }

    // Load Google Maps Places API script
    const scriptId = 'google-maps-places-api';
    if (document.getElementById(scriptId)) {
      // Script is already loading, wait for it
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkGoogleMaps);
          this.setupAutocomplete();
        }
      }, 100);
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.setupAutocomplete();
    };
    script.onerror = () => {
      console.warn('[BuyOnline] Failed to load Google Maps API, falling back to basic search');
      // Fall back to existing search functionality
      this.searchInput.addEventListener('input', (e) => this.onSearch(e));
    };
    document.head.appendChild(script);
  }

  setupAutocomplete() {
    if (!this.searchInput || !window.google || !window.google.maps || !window.google.maps.places) {
      // Fall back to existing search functionality
      this.searchInput.addEventListener('input', (e) => this.onSearch(e));
      return;
    }

    // Create autocomplete instance
    const autocomplete = new window.google.maps.places.Autocomplete(this.searchInput, {
      types: ['address'],
      fields: ['geometry', 'formatted_address', 'name']
    });

    // Store reference to autocomplete for cleanup if needed
    this.autocomplete = autocomplete;

    // Track if a place was selected from autocomplete
    let placeSelected = false;

    // Listen for place selection
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      
      if (!place.geometry || !place.geometry.location) {
        console.warn('[BuyOnline] No geometry data for selected place');
        placeSelected = false;
        // Fall back to text search if no geometry
        this.onSearch({ target: this.searchInput });
        return;
      }

      placeSelected = true;

      // Get lat/long from the selected place
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      // Set user coordinates and sort locations by distance
      this.userCoordinates = {
        latitude: this.parseCoord(lat),
        longitude: this.parseCoord(lng),
      };

      // Sort locations by proximity
      this.sortByProximity();
    });

    // Handle manual typing (when user types but doesn't select from autocomplete)
    // Use a debounce to avoid conflicts with autocomplete
    let inputTimeout;
    this.searchInput.addEventListener('input', (e) => {
      clearTimeout(inputTimeout);
      inputTimeout = setTimeout(() => {
        // Only use text search if no place was selected
        if (!placeSelected) {
          this.onSearch(e);
        }
        placeSelected = false; // Reset for next input
      }, 300);
    });

    // Reset placeSelected flag when input is cleared
    this.searchInput.addEventListener('focus', () => {
      placeSelected = false;
    });
  }

  async syncSelectedStore() {
    const api = window.inventoryInfo?.api;
    if (!api?.getSelectedLocation) return;
    try {
      const loc = await api.getSelectedLocation();
      if (loc?.name) {
        this.setSelectedStore(loc.name);
        if (loc?.id !== undefined && loc?.id !== null) {
          this.currentSelectionId = String(loc.id);
        }
        this.setSelectedDetails({
          address1: loc.address1 || loc.address || '',
          city: loc.city || '',
          region: loc.province || loc.state || loc.region || '',
          postal: loc.zip || loc.postal || '',
          phone: loc.phone || '',
        });
        this.updateSelectionAvailability();
      }
    } catch (error) {
      console.warn('[BuyOnline] Failed to sync selected store', error);
    }
  }

  setSelectedStore(name) {
    const cleaned = this.stripName(name);
    if (this.selectedStoreEl) this.selectedStoreEl.textContent = cleaned || 'Choose a store';
    if (!name) {
      if (this.selectedAddressEl) this.selectedAddressEl.textContent = '';
      if (this.selectedPhoneEl) this.selectedPhoneEl.textContent = '';
    }
  }

  setSelectedDetails({ address1 = '', city = '', region = '', postal = '', phone = '' } = {}) {
    const addressParts = [address1, [city, region].filter(Boolean).join(', '), postal].filter(
      (p) => p && p.trim()
    );
    if (this.selectedAddressEl) {
      this.selectedAddressEl.textContent = addressParts.join(' · ');
    }
    if (this.selectedPhoneEl) {
      this.selectedPhoneEl.textContent = phone || '';
    }
  }

  stripName(label = '') {
    return label.replace(/^\d+\s*-\s*/, '').replace(/\s*\([^)]*\)\s*/g, '').trim();
  }

  async loadLocations() {
    if (!this.endpoint || !this.listEl) {
      // Initialize empty arrays if endpoint/listEl not available
      this.locations = [];
      this.filtered = [];
      this.locationsLoaded = true;
      this.updateSelectionAvailability();
      return;
    }
    this.listEl.innerHTML = '<div class="bo-drawer__empty">Loading stores…</div>';
    this.locationsLoaded = false;
    try {
      const response = await fetch(this.endpoint, { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      // Store raw data for online store detection
      this.rawLocationData = data;
      const normalized = this.normalizeLocations(data) || [];
      this.locations = this.filterHidden(normalized) || [];
      this.mergeExternalCoords();
      this.filtered = this.locations;
      this.locationsLoaded = true;
      this.updateAvailabilityFromLocations();
      this.renderList();
      this.setSelectedDetailsFromList(this.currentSelectionId);
      this.updateSelectionAvailability();
    } catch (error) {
      console.error('[BuyOnline] Failed to load locations', error);
      // Ensure locations is always an array even on error
      this.locations = [];
      this.filtered = [];
      this.locationsLoaded = true;
      this.listEl.innerHTML = '<div class="bo-drawer__empty">Unable to load stores right now.</div>';
      // Still update availability to reflect no pickup availability
      this.updateSelectionAvailability();
    }
  }

  normalizeLocations(data) {
    // Ensure we always return an array
    if (!data) return [];
    // Try variant inventory shape first
    if (Array.isArray(data?.variantLocations) && data.variantLocations.length) {
      const first = data.variantLocations[0];
      const inv = Array.isArray(first?.inventoryLocations) ? first.inventoryLocations : [];
      const parsed = inv
        .map((entry) => {
          const loc = entry?.location || {};
          const id = loc.id ?? loc.locationId ?? loc.location_id;
          if (id == null) return null;
          const name = this.stripName(loc.name || '');
          const city = loc.city || '';
          const region = loc.province || loc.state || loc.region || '';
          const phone = loc.phone || '';
          const address1 = loc.address1 || '';
          const postal = loc.zip || loc.postal || '';
          const quantity = Number(entry.quantity || 0);
          return {
            id,
            name,
            city,
            region,
            phone,
            address1,
            postal,
            quantity,
            latitude: this.parseCoord(loc.latitude),
            longitude: this.parseCoord(loc.longitude),
          };
        })
        .filter(Boolean);
      if (parsed.length) return parsed;
    }

    // Fallback to legacy locations list
    const list = Array.isArray(data?.locations) ? data.locations : Array.isArray(data) ? data : [];
    const result = list
      .map((loc) => {
        const id = loc?.id ?? loc?.locationId ?? loc?.location_id ?? loc?.location?.id;
        if (id == null) return null;
        const name = this.stripName(loc?.name || loc?.location?.name || '');
        const city = loc?.city || loc?.location?.city || '';
        const region = loc?.province || loc?.state || loc?.region || loc?.location?.province || '';
        const phone = loc?.phone || loc?.location?.phone || '';
        const address1 = loc?.address1 || loc?.location?.address1 || '';
        const postal = loc?.zip || loc?.postal || loc?.location?.zip || '';
        const quantity = Number(loc.quantity || 0);
        return {
          id,
          name,
          city,
          region,
          phone,
          address1,
          postal,
          quantity,
          latitude: this.parseCoord(loc.latitude),
          longitude: this.parseCoord(loc.longitude),
        };
      })
      .filter(Boolean);
    
    return result || [];
  }

  parseCoord(value) {
    if (value === null || value === undefined) return null;
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (Number.isNaN(num) || !Number.isFinite(num)) return null;
    return num;
  }

  mergeExternalCoords() {
    if (!Array.isArray(this.locations)) {
      this.locations = [];
      return;
    }
    if (!this.locations.length) return;
    let external = window.storeLocationCoords;
    if (!external) return;
    if (typeof external === 'string') {
      try {
        external = JSON.parse(external);
      } catch (_err) {
        return;
      }
    }

    let sourceList = [];
    if (Array.isArray(external)) {
      sourceList = external;
    } else if (Array.isArray(external?.locations)) {
      sourceList = external.locations;
    } else if (external && typeof external === 'object') {
      Object.values(external).forEach((val) => {
        if (Array.isArray(val)) {
          sourceList = sourceList.concat(val);
        } else {
          sourceList.push(val);
        }
      });
    }

    const coordMap = {};
    sourceList.forEach((item) => {
      const id =
        item?.id ??
        item?.location_id ??
        item?.locationId ??
        item?.location?.id ??
        null;
      if (id === null || id === undefined) return;
      const lat = this.parseCoord(
        item?.latitude ?? item?.lat ?? item?.location?.latitude ?? item?.location?.lat ?? null
      );
      const lng = this.parseCoord(
        item?.longitude ??
          item?.lng ??
          item?.long ??
          item?.lon ??
          item?.location?.longitude ??
          item?.location?.lng ??
          item?.location?.long ??
          item?.location?.lon ??
          null
      );
      coordMap[String(id)] = { latitude: lat, longitude: lng };
    });

    this.locations = this.locations.map((loc) => {
      const coords = coordMap[String(loc.id)];
      if (coords) {
        return {
          ...loc,
          latitude: coords.latitude ?? loc.latitude,
          longitude: coords.longitude ?? loc.longitude,
        };
      }
      return loc;
    });
  }

  renderList() {
    if (!this.listEl) return;
    if (!this.filtered.length) {
      this.listEl.innerHTML = '<div class="bo-drawer__empty">No stores found.</div>';
      return;
    }
    const fragment = document.createDocumentFragment();
    
    // Filter out locations with infinite distance if user coordinates are set
    const locationsToRender = this.filtered.filter((loc) => {
      if (this.userCoordinates && 
          this.userCoordinates.latitude != null && 
          this.userCoordinates.longitude != null &&
          loc.latitude != null && 
          loc.longitude != null) {
        const distanceKm = this.computeDistanceKm(
          this.userCoordinates.latitude,
          this.userCoordinates.longitude,
          loc.latitude,
          loc.longitude
        );
        // Hide locations with infinite distance
        return Number.isFinite(distanceKm);
      }
      // If no user coordinates, show all locations
      return true;
    });
    
    if (!locationsToRender.length) {
      this.listEl.innerHTML = '<div class="bo-drawer__empty">No stores found.</div>';
      return;
    }
    
    locationsToRender.forEach((loc) => {
      // Calculate distance if user coordinates are available
      let distanceHtml = '';
      if (this.userCoordinates && 
          this.userCoordinates.latitude != null && 
          this.userCoordinates.longitude != null &&
          loc.latitude != null && 
          loc.longitude != null) {
        const distanceKm = this.computeDistanceKm(
          this.userCoordinates.latitude,
          this.userCoordinates.longitude,
          loc.latitude,
          loc.longitude
        );
        if (Number.isFinite(distanceKm)) {
          const formattedDistance = this.formatDistance(distanceKm);
          distanceHtml = `<div class="bo-store__distance">${formattedDistance}</div>`;
        }
      }

      const item = document.createElement('article');
      item.className = 'bo-store';
      item.innerHTML = `
        <div class="bo-store__title">${loc.name}</div>
        <div class="bo-store__meta">
          ${distanceHtml}
          <div>${loc.address1 || ''}</div>
          <div>${[loc.city, loc.region].filter(Boolean).join(', ')} ${loc.postal || ''}</div>
          <div>${loc.phone || ''}</div>
          <div class="bo-store__qty ${loc.quantity > 0 ? 'is-available' : 'is-unavailable'}">
            <span class="bo-store__dot"></span>
            <span>${loc.quantity > 0 ? loc.quantity + ' in stock' : 'Out of stock'}</span>
          </div>
        </div>
        <div class="bo-store__actions">
          <button class="bo-store__select" data-id="${loc.id}" type="button">
            <span class="bo-store__select-marker"></span>
            <span>Select this store</span>
          </button>
        </div>
      `;
      const btn = item.querySelector('[data-id]');
      btn?.addEventListener('click', () => this.selectStore(loc));
      fragment.appendChild(item);
    });
    this.listEl.innerHTML = '';
    this.listEl.appendChild(fragment);
  }

  formatDistance(distanceKm) {
    // Format distance: show km for distances >= 1km, meters for < 1km
    if (distanceKm < 1) {
      const meters = Math.round(distanceKm * 1000);
      return `${meters} m`;
    } else if (distanceKm < 10) {
      // Show one decimal place for distances between 1-10km
      return `${distanceKm.toFixed(1)} km`;
    } else {
      // Show whole numbers for distances >= 10km
      return `${Math.round(distanceKm)} km`;
    }
  }

  onSearch(event) {
    // Ensure locations is an array
    if (!Array.isArray(this.locations)) {
      this.locations = [];
    }
    const term = (event.target.value || '').toLowerCase();
    if (!term) {
      this.filtered = this.locations;
    } else {
      this.filtered = this.locations.filter((loc) => {
        return (
          loc.name.toLowerCase().includes(term) ||
          loc.city.toLowerCase().includes(term) ||
          loc.region.toLowerCase().includes(term) ||
          (loc.postal || '').toLowerCase().includes(term)
        );
      });
    }
    this.renderList();
  }

  async selectStore(loc) {
    if (!loc || !loc.id) {
      console.warn('[BuyOnline] Invalid location provided to selectStore');
      return;
    }
    const api = window.inventoryInfo?.api;
    if (api?.selectLocation) {
      try {
        await api.selectLocation(loc.id);
        this.setSelectedStore(loc.name);
        this.currentSelectionId = String(loc.id);
        this.setSelectedDetails({
          address1: loc.address1 || '',
          city: loc.city || '',
          region: loc.region || '',
          postal: loc.postal || '',
          phone: loc.phone || '',
        });
        this.updateSelectionAvailability();
        this.closeDrawer();
        
        // Update the banner store selector
        this.updateBannerStoreSelector(String(loc.id), loc.name);
        
        // Update cart attributes with correct mode
        this.pushCartAttributes();
        
        document.dispatchEvent(
          new CustomEvent('store-selector:change', { 
            detail: { 
              id: String(loc.id), 
              label: loc.name,
              mode: this.selectedMode || 'pickup'
            } 
          })
        );
        return;
      } catch (error) {
        console.warn('[BuyOnline] Failed to select location via API', error);
      }
    }
    this.setSelectedStore(loc.name);
    this.currentSelectionId = String(loc.id);
    this.setSelectedDetails({
      address1: loc.address1 || '',
      city: loc.city || '',
      region: loc.region || '',
      postal: loc.postal || '',
      phone: loc.phone || '',
    });
    this.updateSelectionAvailability();
    this.closeDrawer();
    
    // Update the banner store selector
    this.updateBannerStoreSelector(String(loc.id), loc.name);
    
    // Update cart attributes with correct mode
    this.pushCartAttributes();
  }

  updateBannerStoreSelector(locationId, locationName) {
    // Find the header store selector component
    const headerStoreSelector = document.querySelector('#header-store-selector');
    if (headerStoreSelector && typeof headerStoreSelector.setActiveValue === 'function') {
      // Use the store selector's method to update the banner
      const displayName = this.stripName(locationName);
      headerStoreSelector.setActiveValue(locationId, displayName);
    } else {
      // Fallback: directly update the banner text element
      const bannerValueEl = document.querySelector('.header-store-banner [data-store-selector-value]');
      if (bannerValueEl) {
        const displayName = this.stripName(locationName);
        bannerValueEl.textContent = displayName || locationName;
      }
    }
  }

  updateAvailabilityFromLocations() {
    this.updateSelectionAvailability();
  }

  setStatus(target, isAvailable) {
    if (!target) return;
    target.classList.toggle('is-unavailable', !isAvailable);
    const label = target.querySelector('span:last-child');
    if (label) {
      if (isAvailable) {
        label.textContent = 'Available';
      } else {
        // Check if this is the pickup status and a location is selected
        const isPickupStatus = target === this.pickupStatusEl;
        let unavailableText = 'Unavailable';
        if (isPickupStatus && this.currentSelectionId && this.currentSelectionId !== '-1') {
          const selectedLocation = this.findLocationById(this.currentSelectionId);
          if (selectedLocation && selectedLocation.name) {
            const locationName = this.stripName(selectedLocation.name);
            if (locationName) {
              unavailableText = `Unavailable at ${locationName}`;
            }
          }
        }
        label.textContent = unavailableText;
      }
    }
  }

  setSelectedDetailsFromList(id) {
    if (!id) return;
    const loc = this.findLocationById(id);
    if (!loc) return;
    this.setSelectedDetails({
      address1: loc.address1 || '',
      city: loc.city || '',
      region: loc.region || '',
      postal: loc.postal || '',
      phone: loc.phone || '',
    });
  }

  findLocationById(id) {
    if (!id || !Array.isArray(this.locations)) return null;
    return this.locations.find((loc) => String(loc.id) === String(id)) || null;
  }

  getHiddenLocations() {
    const hidden = window.inventoryInfo?.settings?.hiddenLocation;
    if (!hidden || typeof hidden !== 'object') return {};
    const map = {};
    Object.keys(hidden).forEach((key) => {
      const val = hidden[key];
      if (val) {
        map[String(key)] = true;
      }
    });
    return map;
  }

  getOnlineStoreCodes() {
    // Get online store codes from theme settings
    const setting = window.FoxTheme?.settings?.onlineStoreLocationCodes || '800,211';
    if (!setting) return ['800', '211'];
    return setting.split(',').map(code => code.trim()).filter(Boolean);
  }

  isOnlineStore(location) {
    if (!location || !location.name) return false;
    const codes = this.onlineStoreCodes || this.getOnlineStoreCodes();
    const name = String(location.name);
    return codes.some(code => name.includes(code));
  }

  findOnlineStore() {
    // First check in parsed locations
    const onlineStore = this.locations.find(loc => this.isOnlineStore(loc));
    if (onlineStore) return onlineStore;
    
    // If not found, check raw data from variantLocations
    if (this.rawLocationData?.variantLocations) {
      const codes = this.onlineStoreCodes || this.getOnlineStoreCodes();
      for (const variant of this.rawLocationData.variantLocations) {
        if (Array.isArray(variant?.inventoryLocations)) {
          for (const entry of variant.inventoryLocations) {
            const loc = entry?.location || {};
            const locName = String(loc.name || '');
            // Check if location name contains any online store code
            if (codes.some(code => locName.includes(code))) {
              // Return normalized format
              return {
                id: loc.id ?? loc.locationId ?? loc.location_id,
                name: this.stripName(loc.name || ''),
                city: loc.city || '',
                region: loc.province || loc.state || loc.region || '',
                phone: loc.phone || '',
                address1: loc.address1 || '',
                postal: loc.zip || loc.postal || '',
                quantity: Number(entry.quantity || 0),
                latitude: this.parseCoord(loc.latitude),
                longitude: this.parseCoord(loc.longitude),
              };
            }
          }
        }
      }
    }
    return null;
  }

  filterHidden(list) {
    if (!Array.isArray(list)) return [];
    if (!Object.keys(this.hiddenLocationMap || {}).length) {
      return list;
    }
    const filtered = list.filter((loc) => !this.hiddenLocationMap[String(loc.id)]);
    return filtered;
  }

  updateSelectionAvailability() {
    // Ensure locations is always an array
    if (!Array.isArray(this.locations)) {
      this.locations = [];
    }
    
    const selected = this.findLocationById(this.currentSelectionId);
    // Pickup available if selected location has inventory, or any non-online store has inventory
    const pickupLocations = this.locations.filter(loc => !this.isOnlineStore(loc));
    const pickupAvailable = selected && !this.isOnlineStore(selected) 
      ? Number(selected.quantity) > 0 
      : pickupLocations.some((loc) => Number(loc.quantity) > 0);

    // Ship-to-home follows online store locations (by name pattern, not hardcoded ID)
    const onlineStore = this.findOnlineStore();
    const shipAvailable = onlineStore ? Number(onlineStore.quantity) > 0 : false;
    
    // Overall availability: either pickup or ship
    const anyAvailable = pickupAvailable || shipAvailable;

    this.setStatus(this.pickupStatusEl, pickupAvailable);
    this.setStatus(this.shipStatusEl, shipAvailable);
    const stockTarget = this.stockWrapper || this.stockEl;
    if (this.stockEl) {
      // Show loading only if locations haven't finished loading
      if (!this.locationsLoaded) {
        this.stockEl.textContent = 'Loading...';
      } else if (anyAvailable) {
        // Show "In stock" if either pickup or ship is available
        this.stockEl.textContent = 'In stock';
      } else {
        // Show "Unavailable" only if both pickup and ship are unavailable
        // If pickup is unavailable and a location is selected, append location name
        let unavailableText = 'Unavailable';
        if (!pickupAvailable && this.currentSelectionId && this.currentSelectionId !== '-1') {
          const selectedLocation = this.findLocationById(this.currentSelectionId);
          if (selectedLocation && selectedLocation.name && !this.isOnlineStore(selectedLocation)) {
            const locationName = this.stripName(selectedLocation.name);
            if (locationName) {
              unavailableText = `Unavailable at ${locationName}`;
            }
          }
        }
        this.stockEl.textContent = unavailableText;
      }
    }
    if (stockTarget) {
      // Update classes based on overall availability
      stockTarget.classList.toggle('is-unavailable', !anyAvailable);
      stockTarget.classList.toggle('is-available', anyAvailable);
    }
    this.pickupAvailable = pickupAvailable;
    this.shipAvailable = shipAvailable;
    this.updateTabState();
    this.updatePickupRowVisibility();
    
    // Update cart attributes after availability is determined and locations are loaded
    if (this.locationsLoaded) {
      this.pushCartAttributes();
    }
  }

  updatePickupRowVisibility() {
    // Check if online location is selected or no location is set
    const selectedLocation = this.findLocationById(this.currentSelectionId);
    const isOnlineLocationSelected = selectedLocation ? this.isOnlineStore(selectedLocation) : false;
    const noLocationSet = !this.currentSelectionId || this.currentSelectionId === null || this.currentSelectionId === '' || this.currentSelectionId === '-1';
    
    // Hide pickup-related rows when:
    // 1. Pickup is not available (only online stores available)
    // 2. Ship mode is selected
    // 3. Online location is selected
    // 4. No location is set
    const shouldHidePickupRows = !this.pickupAvailable || 
                                 isOnlineLocationSelected || 
                                 noLocationSet;
    
    if (this.pickupRows && this.pickupRows.length > 0) {
      this.pickupRows.forEach((row) => {
        if (shouldHidePickupRows) {
          row.style.display = 'none';
        } else {
          row.style.display = '';
        }
      });
    }
    
    // Hide the entire body if pickup is not available and we're in pickup mode
    // (This prevents showing confusing pickup options when only online stores are available)
    if (this.bodyEl && this.selectedMode === 'pickup' && !this.pickupAvailable) {
      this.bodyEl.style.display = 'none';
    } else if (this.bodyEl) {
      this.bodyEl.style.display = '';
    }
  }

  handleNearby() {
    if (!navigator.geolocation) {
      console.warn('[BuyOnline] Geolocation not supported');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.userCoordinates = {
          latitude: this.parseCoord(pos.coords.latitude),
          longitude: this.parseCoord(pos.coords.longitude),
        };
        this.sortByProximity();
      },
      (err) => {
        console.warn('[BuyOnline] Geolocation error', err);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 30000 }
    );
  }

  sortByProximity() {
    if (!this.userCoordinates || this.userCoordinates.latitude == null || this.userCoordinates.longitude == null)
      return;
    const { latitude: userLat, longitude: userLng } = this.userCoordinates;
    const withDistances = this.locations.map((loc) => ({
      location: loc,
      distance: this.computeDistanceKm(userLat, userLng, loc.latitude, loc.longitude),
    }));
    // Filter out locations with infinite distance
    const known = withDistances.filter((entry) => Number.isFinite(entry.distance));
    known.sort((a, b) => a.distance - b.distance);
    // Only include locations with finite distances
    this.locations = known.map((entry) => entry.location);
    this.filtered = this.locations;
    this.renderList();
  }

  computeDistanceKm(lat1, lon1, lat2, lon2) {
    if (
      lat1 == null ||
      lon1 == null ||
      lat2 == null ||
      lon2 == null ||
      Number.isNaN(lat1) ||
      Number.isNaN(lon1) ||
      Number.isNaN(lat2) ||
      Number.isNaN(lon2)
    ) {
      return Number.POSITIVE_INFINITY;
    }
    const toRad = (deg) => (deg * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  selectMode(mode, force = false) {
    if (mode === 'pickup' && !this.pickupAvailable && !force) return;
    if (mode === 'ship' && !this.shipAvailable && !force) return;
    
    // Track if user explicitly selected a mode (not auto-switched)
    if (!force) {
      if (mode === 'ship') {
        this.userSelectedShip = true;
        this.userSelectedPickup = false;
      } else if (mode === 'pickup') {
        this.userSelectedPickup = true;
        this.userSelectedShip = false;
      }
    }
    
    this.selectedMode = mode;
    this.updateTabState();
    this.updatePickupRowVisibility();
    this.sampleProductwithLocation();
    this.pushCartAttributes();
  }

  updateTabState() {
    const pickupTab = this.pickupTab;
    const shipTab = this.shipTab;
    const pickupActive = this.selectedMode === 'pickup';
    const shipActive = this.selectedMode === 'ship';
    if (pickupTab) {
      pickupTab.classList.toggle('is-active', pickupActive);
      pickupTab.setAttribute('aria-selected', pickupActive ? 'true' : 'false');
      pickupTab.classList.toggle('is-unavailable', !this.pickupAvailable);
      pickupTab.setAttribute('aria-disabled', this.pickupAvailable ? 'false' : 'true');
    }
    if (shipTab) {
      shipTab.classList.toggle('is-active', shipActive);
      shipTab.setAttribute('aria-selected', shipActive ? 'true' : 'false');
      shipTab.classList.toggle('is-unavailable', !this.shipAvailable);
      shipTab.setAttribute('aria-disabled', this.shipAvailable ? 'false' : 'true');
    }

    // Auto-switch if current mode is unavailable but the other is available
    // Only auto-switch after locations are loaded to avoid premature switching
    if (this.locationsLoaded) {
      // Prefer pickup if available (unless user explicitly selected ship)
      if (this.pickupAvailable && !this.userSelectedShip && this.selectedMode !== 'pickup') {
        this.selectMode('pickup', true);
      } else if (this.selectedMode === 'pickup' && !this.pickupAvailable && this.shipAvailable) {
        // Only auto-switch to ship if pickup is not available
        this.selectMode('ship', true);
      } else if (this.selectedMode === 'ship' && !this.shipAvailable && this.pickupAvailable) {
        // Switch back to pickup if ship becomes unavailable and pickup is available
        if (!this.userSelectedShip) {
          this.selectMode('pickup', true);
        }
      }
    }
  }

  sampleProductwithLocation() {
    const loc =
      this.selectedMode === 'ship'
        ? this.findOnlineStore() || this.findLocationById(this.currentSelectionId)
        : this.findLocationById(this.currentSelectionId);
  }

  pushCartAttributes() {
    if (typeof this.updateCartAttributes !== 'function') return;
    // Ensure locations is an array before searching
    if (!Array.isArray(this.locations)) {
      this.locations = [];
    }
    const loc =
      this.selectedMode === 'ship'
        ? this.findOnlineStore() || this.findLocationById(this.currentSelectionId)
        : this.findLocationById(this.currentSelectionId);
    const payload = {
      mode: this.selectedMode || '',
      id: loc?.id != null ? String(loc.id) : '',
      name: loc?.name || '',
    };
    this.updateCartAttributes(payload);
  }

  openDrawer() {
    if (!this.drawer) return;
    this.drawer.removeAttribute('hidden');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('bo-drawer-open');
    this.searchInput?.focus();
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.hidden = true;
    this.drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('bo-drawer-open');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-buy-online]').forEach((el) => {
    const instance = new BuyOnline(el);
    // Store instance on element for external access
    el.__buyOnlineInstance = instance;
  });
});

// Shared helper to persist selection into cart attributes without clobbering existing attributes
window.updatePickupCartAttributes = async function updatePickupCartAttributes(selection = {}) {
  try {
    const mode = selection.mode || '';
    const id = selection.id || '';
    const name = selection.name || '';
    if (!mode && !id && !name) return;

    const currentCart = await fetch('/cart.js').then((r) => r.json());
    const attributes = { ...(currentCart?.attributes || {}) };
    if (mode) attributes['pickup_mode'] = mode;
    if (id) attributes['pickup_location_id'] = id;
    if (name) attributes['pickup_location_name'] = name;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
  } catch (error) {
    console.warn('[updatePickupCartAttributes] Failed to update cart attributes', error);
  }
};

